import React, { type ReactNode, type PropsWithChildren, useMemo } from 'react';
import { graphql, usePreloadedQuery, type PreloadedQuery } from 'react-relay';
import { Flex } from '@atlaskit/primitives/compiled';
import { SpotlightTarget } from '@atlaskit/onboarding';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import FilterRefinement from '@atlassian/jira-filter-refinement/src/ui/FilterRefinement.tsx';
import type { Props as FilterRefinementProps } from '@atlassian/jira-filter-refinement/src/ui/types.tsx';
import { componentWithFG } from '@atlassian/jira-feature-gate-component/src/index.tsx';
import type { JQLBuilderWithAIFilterRefinementQuery } from './__generated__/JQLBuilderWithAIFilterRefinementQuery.graphql';
import messages from './messages.tsx';

type Props = Pick<
	FilterRefinementProps,
	'defaultJql' | 'onReset' | 'onSearch' | 'projectContext' | 'jqlMessages'
> & {
	beforeControls?: ReactNode;
	filterRefinementQueryReference: PreloadedQuery<JQLBuilderWithAIFilterRefinementQuery>;
	isSearching?: boolean;
};

const MaybeSpotlightTarget = componentWithFG(
	'jira_paid_list_nin_merge',
	SpotlightTarget,
	({ children }: PropsWithChildren<{}>) => children,
);

export const JQLBuilderWithAIFilterRefinement = ({
	defaultJql,
	onSearch,
	beforeControls,
	filterRefinementQueryReference,
	projectContext,
	onReset,
	jqlMessages,
	isSearching,
}: Props): React.JSX.Element => {
	const filterRefinementData = usePreloadedQuery<JQLBuilderWithAIFilterRefinementQuery>(
		graphql`
			query JQLBuilderWithAIFilterRefinementQuery(
				$cloudId: ID!
				$isFilterRefinementEnabled: Boolean!
				$scope: JiraJqlScopeInput
			) {
				jira {
					...FilterRefinement
						@arguments(assignees: 50, cloudId: $cloudId, scope: $scope)
						@include(if: $isFilterRefinementEnabled)
				}
			}
		`,
		filterRefinementQueryReference,
	);

	const { formatMessage } = useIntl();

	const scope = useMemo(() => {
		return projectContext
			? {
					project: {
						projectKey: projectContext,
					},
				}
			: undefined;
	}, [projectContext]);

	return (
		<MaybeSpotlightTarget name="jql-builder.ui.jql-builder.editor">
			<Flex gap="space.075" justifyContent="start" alignItems="center">
				{beforeControls}
				<FilterRefinement
					defaultJql={defaultJql}
					changeBoardingView={fg('changeboarding-filter-refinement-in-nin') ? 'list' : undefined}
					fragmentRef={filterRefinementData.jira}
					onSearch={onSearch}
					searchFieldPlaceholder={formatMessage(messages.searchWork)}
					isModeSwitcherEnabled
					isResponsive={false}
					projectContext={projectContext}
					onReset={onReset}
					scope={scope}
					jqlMessages={jqlMessages}
					isSearching={isSearching}
				/>
			</Flex>
		</MaybeSpotlightTarget>
	);
};
